import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	HostListener,
	input,
	model,
	OnDestroy,
	OnInit,
	signal,
	untracked,
} from '@angular/core';
import { SeoMetadataService } from '@uc/utilities';
import { formatSearchTerm, isPostgraduate } from '../../../utils/utils';
import { Store } from '@ngrx/store';
import {
	CourseSearchErrorsEnum,
	CourseSearchLocationGAEnum,
	DegreeLevelOption,
	Offset,
	ProductEnum,
} from '@uc/web/shared/data-models';
import {
	fetchClearingCourses,
	fetchPaginatedClearingCourses,
	updateClearingCourseSearchPayload,
} from '../../../data-access/+store/course-search.actions';
import {
	getStateClearingCourses,
	getStateCourseSearchError,
	getStateCourseSearchFilterPills,
	getStateCourseSearchIsLoading,
	getStateCourseSearchIsPaginationLoading,
} from '../../../data-access/+store/course-search.selectors';
import { ClearingCourseSearch } from '../../../models/clearing-course-search.models';
import {
	clearingFAQ,
	CourseGTMService,
	FilterTogglerService,
	HECoSSubjectValidatorService,
	NA,
} from '@uc/web/core';
import {
	CourseSearchBarComponent,
	FaqAccordionComponent,
	MainHeaderComponent,
	UniversitiesSkeletonComponent,
} from '@web/shared/ui';
import { CourseSearchErrorComponent } from '../../../components/course-search-error/course-search-error.component';
import {
	getDegreeLevelType,
	getSearchFilterResetPayload,
	mapParamsToPayload,
} from '../../../utils/course-search-payload.utils';
import { CommonModule, Location } from '@angular/common';
import {
	BASIC_DEGREE_LEVEL_OPTIONS,
	CLEARING_DEGREE_LEVEL_OPTION,
	DegreeLevelService,
} from '@uc/web/shared/data-access';
import {
	FilterDarkOverlayComponent,
	FilterSortWidgetComponent,
} from '@uc/web/shared/feature/feature-filter';
import { ClearingCourseSearchFilterComponent } from '../../../components/filter/clearing-course-search-filter/clearing-course-search-filter.component';
import { CourseSearchPaginationComponent } from '../../../components/course-search-pagination/course-search-pagination.component';
import { CourseSearchResultComponent } from '../../../components/course-search-result/course-search-result.component';
import { CourseListClearingComponent } from '../../../components/clearing/course-list-clearing/course-list-clearing.component';
import { CourseSearchPayloadValue } from '../../../models/course-search-payload.models';
import {
	getCourseSearchSegments,
	getRouteSegmentsAndQueryParams,
} from '../../../utils/route.utils';
import { Router } from '@angular/router';
import {
	queryParamsToObject,
	sortQueryParamsByKey,
} from '../../../utils/query-params.utils';
import { CLEARING_ENABLED_COPY } from '../../../static/clearing-course-search.properties';
import {
	formatClearingTitle,
	formatClearingEnabledDesc,
} from '../../../utils/clearing.utils';
import { CourseSearchScrollerService } from '../../../services/course-search-scroller.service';
import { Meta } from '@angular/platform-browser';
import { DirectoryCardCtaComponent } from '../../../components/directory-card-cta/directory-card-cta.component';
import { InsertRecommendedCoursesService } from '../../../services/insert-recommended-courses.service';
import { LOCATIONS } from '../../../static/courses-filter.properties';
import { CLEARING_COURSE_SEARCH_DEFAULT_PAYLOAD } from '../../../static/course-search-payload.properties';
import { CLEARING_DEGREE_LEVEL_OPTIONS } from '../../../static/courses-hub.properties';

@Component({
	selector: 'uc-clearing-course-search',
	templateUrl: './clearing-course-search.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		UniversitiesSkeletonComponent,
		CourseSearchErrorComponent,
		CommonModule,
		MainHeaderComponent,
		CourseSearchBarComponent,
		FilterDarkOverlayComponent,
		ClearingCourseSearchFilterComponent,
		CourseSearchPaginationComponent,
		CourseSearchResultComponent,
		CourseListClearingComponent,
		FilterSortWidgetComponent,
		FaqAccordionComponent,
		DirectoryCardCtaComponent,
	],
})
export class ClearingCourseSearchComponent implements OnInit, OnDestroy {
	subject = input.required<string>();
	degree = input<string>();
	location = input<string>();
	studyMode = input<string>();
	ucaspts = input<string>();
	page = model<string>();
	search = input<string>();

	studyLevelOptions: DegreeLevelOption[] = [
		CLEARING_DEGREE_LEVEL_OPTION,
		...BASIC_DEGREE_LEVEL_OPTIONS,
	];
	currentYear = new Date().getFullYear();
	readonly faq = clearingFAQ;
	readonly headerSearchBar = CourseSearchLocationGAEnum.HeaderSearchBar;

	isLoading = this._store.selectSignal(getStateCourseSearchIsLoading);
	isPaginationLoading = this._store.selectSignal(
		getStateCourseSearchIsPaginationLoading,
	);
	error = this._store.selectSignal(getStateCourseSearchError);

	isHECoSSubject = signal(false);
	locationTitle = signal(NA);

	selectedDegree = computed(() => {
		const degree = this.degree() ?? 'clearing';
		untracked(() => this._setClearingDegreeLevel(degree));
		return degree;
	});
	searchTerm = computed(() => this.subject() ?? this.search() ?? '');
	degreeLevel = computed(() => this._degreeLevelSrv.selectedDegreeLevel());

	headerTitle = computed(() => {
		if (this.courses()?.data?.length === 0) {
			const formattedSearchTerm = formatSearchTerm(this.searchTerm());
			return `No results for ${formattedSearchTerm.trim()}`;
		}

		return formatClearingTitle(
			CLEARING_ENABLED_COPY.header_title,
			this.subject(),
			this.currentYear,
		);
	});

	headerDescription = computed(() => {
		if (this.courses()?.data?.length === 0) {
			const formattedSearchTerm = formatSearchTerm(this.searchTerm());
			return `It looks like we have no courses under ${formattedSearchTerm.trim()}. Enter a new search and try again.`;
		}

		return formatClearingEnabledDesc(
			CLEARING_ENABLED_COPY.header_desc,
			this.subject(),
			this.courses()?.total,
			this.courses()?.universities_count,
		);
	});

	showLoadMore = computed(
		() => !!this.courses()?.data?.length && !!this.courses()?.has_more_pages,
	);

	courses = computed(() => {
		const courses = this._store.selectSignal(getStateClearingCourses);
		const coursesData =
			this._insertRecommendedCoursesSrv.insertRecommendedCourses(courses());

		return coursesData as ClearingCourseSearch | null;
	});

	filterPillsCount = computed(() => {
		const pills = this._store.selectSignal(getStateCourseSearchFilterPills);
		return pills().length;
	});

	shouldDisplayCourses = computed(() => {
		const hasCourses = !!this.courses()?.data?.length;
		const result = (hasCourses && !this.isHECoSSubject()) || this.isHECoSSubject();

		if (!result) {
			this._courseSearchScrollerSrv.scrollToTop();
		}

		return result;
	});

	@HostListener('window:popstate', ['$event'])
	onPopstate(event: PopStateEvent) {
		const location = (event.currentTarget as Window)?.location;
		this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			this._router.navigateByUrl(`${location.pathname}${location.search}`);
		});
	}

	private _errorType = computed(() => {
		if (this._courseGTMService.noSearchTermError()) {
			return CourseSearchErrorsEnum.NoSearchQuery;
		} else if (!this.shouldDisplayCourses()) {
			return CourseSearchErrorsEnum.NoResults;
		} else if (this.courses()?.data?.length === 0) {
			return CourseSearchErrorsEnum.TooManyFilters;
		}
		return false;
	});

	private _metadataEffect = effect(() => {
		const title = formatClearingTitle(
			CLEARING_ENABLED_COPY.meta_title,
			this.subject(),
			this.currentYear,
		);

		const description = formatClearingEnabledDesc(
			CLEARING_ENABLED_COPY.meta_desc,
			this.subject(),
			this.courses()?.total,
			this.courses()?.universities_count,
		);
		this._seoSrv.updateTitle(title);
		this._seoSrv.updateDescription(description);
	});

	private _locations = structuredClone(LOCATIONS);
	private _courseGaEffect = effect(() => {
		const isEmptySearch = this._errorType() === CourseSearchErrorsEnum.NoSearchQuery;
		if (!this.isLoading() || isEmptySearch) {
			untracked(() => {
				const searchLocation = this._courseGTMService.getSearchLocation();
				const isAutosuggest = this._courseGTMService.getAutosuggest();
				const searchTerm = isEmptySearch ? '' : this.searchTerm();
				const locationTitle = this._locations.find(
					(loc) => loc.link === this.location(),
				)?.name;
				this.locationTitle.set(locationTitle ?? NA);

				this._courseGTMService.courseSearch(
					searchTerm,
					searchLocation,
					this.isHECoSSubject(),
					this.degreeLevel().label,
					this._errorType(),
					isAutosuggest,
					locationTitle,
				);
			});
		}
	});

	// initiates search when user is on clearing course search page & uses nav autosuggest
	private _navbarAutosuggestEffect = effect(async () => {
		const searchLocation = this._courseGTMService.getSearchLocationWithoutReset();

		if (searchLocation !== CourseSearchLocationGAEnum.TopSearchBar) return;

		await this.onSearch({
			searchTerm: this.searchTerm(),
			degree: untracked(() => this.selectedDegree()),
		});
	});

	constructor(
		public filterTogglerSrv: FilterTogglerService,
		private _store: Store,
		private _router: Router,
		private _location: Location,
		private _meta: Meta,
		private _seoSrv: SeoMetadataService,
		private _insertRecommendedCoursesSrv: InsertRecommendedCoursesService,
		private _hecosSubjectValidatorSrv: HECoSSubjectValidatorService,
		private _degreeLevelSrv: DegreeLevelService,
		private _courseGTMService: CourseGTMService,
		private _courseSearchScrollerSrv: CourseSearchScrollerService,
	) {}

	ngOnInit(): void {
		if (!this.subject()) {
			this._meta.updateTag({ name: 'robots', content: 'noindex' });
		}

		this._validateHECoSSubject(this.searchTerm());
		this._handleDegreeLevel();

		const payload = mapParamsToPayload(
			this.selectedDegree(),
			this.location(),
			this.studyMode(),
			this.ucaspts(),
			this.searchTerm(),
			this.page(),
		);

		this._store.dispatch(updateClearingCourseSearchPayload({ payload }));
		this._store.dispatch(fetchClearingCourses());
	}

	ngOnDestroy(): void {
		this._metadataEffect.destroy();
		this._courseGaEffect.destroy();
		this._navbarAutosuggestEffect.destroy();
	}

	onFilterChange(payload: Partial<CourseSearchPayloadValue>) {
		this._store.dispatch(updateClearingCourseSearchPayload({ payload }));
		this._store.dispatch(fetchClearingCourses());
	}

	onFilterReset() {
		const payload = getSearchFilterResetPayload(this.searchTerm(), 'clearing');
		this._store.dispatch(updateClearingCourseSearchPayload({ payload }));
		this._store.dispatch(fetchClearingCourses());
	}

	onRelatedSubject(subject: string) {
		const defaultPayload = { ...CLEARING_COURSE_SEARCH_DEFAULT_PAYLOAD };
		const payload = {
			...defaultPayload,
			degreeLevel: getDegreeLevelType(this.selectedDegree()),
			name: subject.replace(/-/g, ' ').toLowerCase(),
		};

		this._store.dispatch(updateClearingCourseSearchPayload({ payload }));
		this._store.dispatch(fetchClearingCourses());
	}

	onLoadMore(page: number) {
		const payload = { page, offset: Offset.On };
		this._store.dispatch(updateClearingCourseSearchPayload({ payload }));
		this._store.dispatch(fetchPaginatedClearingCourses());

		const route = this._router.url.split('?');
		const queryParamsString = route[1] ? `${route[1]}&page=${page}` : `page=${page}`;
		const queryParamsObject = queryParamsToObject(queryParamsString);

		if (queryParamsObject) {
			const sortedQueryParams = sortQueryParamsByKey(queryParamsObject);

			this.page.set(page.toString());

			const queryParams = new URLSearchParams(sortedQueryParams).toString();

			this._location.go(route[0], `?${queryParams}`);
		}
	}

	async onSearch(autosuggest: { searchTerm: string; degree?: string }) {
		if (!autosuggest.searchTerm) return;
		const searchTerm = autosuggest.searchTerm.trim();
		const degree = autosuggest.degree || this.selectedDegree();

		if (degree !== ProductEnum.Clearing) {
			await this.onStudyLevelSelect(degree, searchTerm);
			return;
		}

		this._updateCourseSearchPayload(searchTerm, degree);
		this._store.dispatch(fetchClearingCourses());

		const segments = await this._determineSegments(
			degree,
			searchTerm,
			this.location(),
		);

		const queryParams = this._updateSearchParam(searchTerm, this.isHECoSSubject());
		this._router.navigate([...segments], { queryParams });
	}

	async onStudyLevelSelect(link?: string, searchTerm?: string) {
		const subject = searchTerm ?? this.searchTerm();
		const degree = isPostgraduate(link) || !this.degree() ? link : this.degree();

		const segments = await this._determineSegments(degree, subject, this.location());
		const searchParams = this._updateSearchParam(subject, this.isHECoSSubject());
		const queryParams = this._deleteClearingQueryParams(searchParams, degree);
		this._router.navigate([...segments], { queryParams });
	}

	private async _validateHECoSSubject(searchTerm: string) {
		const isHECoSSubject =
			await this._hecosSubjectValidatorSrv.isHECoSSubject(searchTerm);
		this.isHECoSSubject.set(isHECoSSubject);
	}

	private _handleDegreeLevel() {
		const selectedDegree = this.studyLevelOptions.find(
			(degree) => degree.link === this.selectedDegree(),
		);
		this._degreeLevelSrv.setDegreeLevels(this.studyLevelOptions);
		this._degreeLevelSrv.setSelectedDegreeLevel(
			selectedDegree ?? this.studyLevelOptions[0],
		);
	}

	private _updateCourseSearchPayload(searchTerm: string, degree: string) {
		const name = searchTerm.replace(/-/g, ' ').toLowerCase();
		const degreeLevel = getDegreeLevelType(degree);
		const payload = { name, degreeLevel };

		this._store.dispatch(updateClearingCourseSearchPayload({ payload }));
	}

	private _updateSearchParam(searchTerm: string, isHECoSSubject: boolean) {
		const { queryParams = {} } = getRouteSegmentsAndQueryParams(this._router.url);

		if (isHECoSSubject) {
			delete queryParams['search'];
		} else {
			const formattedSearchTerm = searchTerm.replace(/ /g, '-').toLowerCase();
			queryParams['search'] = formattedSearchTerm;
		}

		return sortQueryParamsByKey(queryParams);
	}

	private _deleteClearingQueryParams(
		queryParams: {
			[key: string]: string;
		},
		degree?: string,
	) {
		if (isPostgraduate(degree) && queryParams['ucaspts']) {
			delete queryParams['ucaspts'];
		}
		if (queryParams['degree']) {
			delete queryParams['degree'];
		}
		if (queryParams['location']) {
			delete queryParams['location'];
		}

		return queryParams;
	}

	private async _determineSegments(
		degree: string | undefined,
		subject: string | undefined,
		location: string | undefined,
	) {
		const _subject = subject ?? '';
		await this._validateHECoSSubject(_subject);
		return getCourseSearchSegments(this.isHECoSSubject(), degree, _subject, location);
	}

	private _setClearingDegreeLevel(selectedDegree?: string) {
		const clearing: DegreeLevelOption = {
			label: 'Clearing',
			link: 'clearing',
		};

		selectedDegree ??= this.selectedDegree();

		const selectedDegreeLevel = CLEARING_DEGREE_LEVEL_OPTIONS.find((degree) =>
			selectedDegree === 'degrees'
				? degree.label === "Bachelor's"
				: degree.link.includes(selectedDegree),
		);
		const degreeLevelOption = selectedDegreeLevel ?? clearing;

		this._degreeLevelSrv.selectedClearingDegreeLevel.set(degreeLevelOption);
		this._degreeLevelSrv.shouldQueryClearingAPI.set(true);
	}
}
